import React, { useState, useEffect } from 'react';
import { csrfFetch } from '../../store/csrf';
import './MeetAndGreetModal.css';
import DogCard from '../../components/DogCard';
import ModalWrapper from '../ModalWrapper';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';

const DRAFT_KEY = 'meetAndGreetDraft';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Format raw digits as (XXX) XXX-XXXX for display while typing
const formatPhoneDisplay = (digits) => {
  const d = (digits || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const defaultDog = () => ({
  id: Date.now() + Math.random(),
  name: '',
  breeds: [],
  years: '',
  months: '',
});

const MeetAndGreetModal = ({ isOpen, onClose, onSuccess }) => {
  const [ownerName, setOwnerName] = useState('');
  const [phone, setPhone] = useState(''); // stored as raw digits
  const [email, setEmail] = useState('');
  const [dogs, setDogs] = useState([defaultDog()]);
  const [currentDogIndex, setCurrentDogIndex] = useState(0);
  const [meetDateTime, setMeetDateTime] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  // On mount: restore saved draft if present and not stale
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (!saved) return;
      const draft = JSON.parse(saved);
      const age = Date.now() - (draft.timestamp || 0);
      if (age > DRAFT_MAX_AGE_MS) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      setOwnerName(draft.ownerName || '');
      setPhone(draft.phone || '');
      setEmail(draft.email || '');
      setMeetDateTime(draft.meetDateTime || '');
      if (Array.isArray(draft.dogs) && draft.dogs.length > 0) {
        setDogs(draft.dogs.map((d) => ({ ...defaultDog(), ...d })));
      }
      setShowDraftBanner(true);
    } catch (_) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = (updates = {}) => {
    try {
      const draft = {
        ownerName: updates.ownerName !== undefined ? updates.ownerName : ownerName,
        phone: updates.phone !== undefined ? updates.phone : phone,
        email: updates.email !== undefined ? updates.email : email,
        meetDateTime: updates.meetDateTime !== undefined ? updates.meetDateTime : meetDateTime,
        dogs: updates.dogs !== undefined ? updates.dogs : dogs,
        timestamp: Date.now(),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch (_) {}
  };

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
  };

  const validatePhone = (p) => {
    if (!p) return false;
    return p.replace(/\D/g, '').length === 10;
  };

  const validateEmail = (e) => {
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  };

  // At least one way to reach them — matches the Contact form. Requiring BOTH
  // turned away anyone who only wanted to leave a phone number.
  const validateContactMethods = () => phone.trim().length > 0 || email.trim().length > 0;

  const hasValue = (v) => v !== '' && v !== null && v !== undefined;

  // Returns { index, message } for the first dog that fails validation, or null.
  // Age is valid as long as EITHER years or months is provided — a 6-month-old
  // puppy is entered as Months: 6 with Years left blank (treated as 0). Blank
  // Years should never block a submission.
  const findInvalidDog = () => {
    for (let i = 0; i < dogs.length; i++) {
      const dog = dogs[i];
      if (!dog.name || !dog.name.trim()) {
        return { index: i, message: `Please enter a name for dog #${i + 1}.` };
      }
      if (!dog.breeds || !dog.breeds.length) {
        return { index: i, message: `Please select at least one breed for ${dog.name || `dog #${i + 1}`}.` };
      }
      if (!hasValue(dog.years) && !hasValue(dog.months)) {
        return {
          index: i,
          message: `Please enter an age for ${dog.name || `dog #${i + 1}`} — years, months, or both. Under 1 year? Just fill in Months.`,
        };
      }
    }
    return null;
  };

  const handleDogChange = (index, field, value) => {
    setError('');
    const newDogs = [...dogs];
    newDogs[index] = { ...newDogs[index], [field]: value };
    setDogs(newDogs);
    saveDraft({ dogs: newDogs });
  };

  const removeDog = (index) => {
    if (dogs.length <= 1) return;
    const newDogs = dogs.filter((_, i) => i !== index);
    setDogs(newDogs);
    if (currentDogIndex >= newDogs.length) {
      setCurrentDogIndex(newDogs.length - 1);
    }
    saveDraft({ dogs: newDogs });
  };

  const addDog = () => {
    const newDogs = [...dogs, defaultDog()];
    setDogs(newDogs);
    setCurrentDogIndex(dogs.length);
    saveDraft({ dogs: newDogs });
  };

  const handleNextDog = () => {
    if (currentDogIndex < dogs.length - 1) setCurrentDogIndex(currentDogIndex + 1);
  };

  const handlePrevDog = () => {
    if (currentDogIndex > 0) setCurrentDogIndex(currentDogIndex - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!ownerName || !meetDateTime || dogs.length === 0) {
      setError('Please fill out all fields.');
      return;
    }
    if (!validateContactMethods()) {
      setError('Please provide a phone number or an email address.');
      return;
    }
    // Only validate the contact methods they actually filled in.
    if (phone.trim() && !validatePhone(phone)) {
      setError('Phone number must be exactly 10 digits.');
      return;
    }
    if (email.trim() && !validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    const invalidDog = findInvalidDog();
    if (invalidDog) {
      setCurrentDogIndex(invalidDog.index); // bring the offending dog into view
      setError(invalidDog.message);
      return;
    }
    const selectedDateTime = new Date(meetDateTime);
    if (selectedDateTime <= new Date()) {
      setError('Please select a future date and time.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const dogsData = dogs.map((dog) => ({
      name: dog.name,
      breeds: dog.breeds,
      years: parseInt(dog.years, 10) || 0,
      months: parseInt(dog.months, 10) || 0,
    }));

    const formattedPhone = phone ? formatPhoneNumber(phone) : '';
    const primaryContact = formattedPhone || email;

    const meetDateTimeISO = selectedDateTime.toISOString();
    const meetDateTimeDisplay = selectedDateTime.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'short',
    });

    const backendData = {
      source: 'Meet and Greet Form',
      name: ownerName,
      contact: primaryContact,
      phone: formattedPhone || null,
      email: email || null,
      meetDateTime: meetDateTimeISO,
      dogs: dogsData,
    };

    const formspreeData = { ...backendData, meetDateTime: meetDateTimeDisplay };

    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify(backendData));

      const response = await csrfFetch('/api/public/leads', {
        method: 'POST',
        body: formData,
      });

      // Handle rate limit gracefully — show friendly message, not generic error
      if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "You've already submitted a request. We'll be in touch soon!");
        return;
      }

      if (response.status === 201) {
        fetch('https://formspree.io/f/xnnjoavw', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formspreeData),
        }).catch(() => {});

        clearDraft();
        if (onSuccess) onSuccess();
        onClose();
      } else {
        setError('There was an error scheduling your meet and greet. Please try again.');
      }
    } catch (err) {
      console.error('Error during submission:', err);
      setError('There was an error scheduling your meet and greet. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      className="modal-dark"
      headerContent={
        <>
          <h2 className="meetandgreetmodal-header">
            Schedule a Meet &amp; Greet
          </h2>
          <p className="meetandgreetmodal-subheader">
            We&apos;d love to meet you and your pup!
          </p>
        </>
      }
      bottomButtonsPresent={true}
      customStyles={{ container: { maxWidth: '720px' } }}
      preventClose={isSubmitting}
    >
      <div className="meetandgreetmodal-content-wrap">
        {isSubmitting && (
          <div className="meetandgreetmodal-loading-overlay">
            <div className="meetandgreetmodal-spinner" />
            <p className="meetandgreetmodal-loading-text">
              Submitting your request...
            </p>
          </div>
        )}

        {/* Subtle draft restore banner */}
        {showDraftBanner && (
          <div className="meetandgreetmodal-draft-banner">
            <span>We found your saved progress. Pick up where you left off?</span>
            <button
              type="button"
              className="meetandgreetmodal-draft-dismiss"
              onClick={() => setShowDraftBanner(false)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        <div
          className="meetandgreetmodal-error-container"
          role={error ? 'alert' : undefined}
          aria-live="polite"
        >
          {error && (
            <>
              <p className="meetandgreetmodal-error">{error}</p>
              <button
                type="button"
                className="meetandgreetmodal-try-again"
                onClick={() => setError('')}
              >
                Try Again
              </button>
            </>
          )}
        </div>

        <form className="meetandgreetmodal-form" onSubmit={handleSubmit} noValidate>
          {/* Owner Info Row. Inputs have sr-only labels for AT and rely on placeholders
              for visual hint only. */}
          <div className="meetandgreetmodal-owner-row">
            <label htmlFor="ownerName" className="sr-only">Your name</label>
            <input
              id="ownerName"
              name="ownerName"
              type="text"
              placeholder="Your Name"
              className="meetandgreetmodal-input"
              value={ownerName}
              onChange={(e) => {
                setOwnerName(e.target.value);
                setError('');
                saveDraft({ ownerName: e.target.value });
              }}
              autoComplete="name"
              required
              aria-required="true"
            />
            <div className="meetandgreetmodal-contact-grid">
              <label htmlFor="phone" className="sr-only">Phone number</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Phone Number"
                className="meetandgreetmodal-input phone"
                value={formatPhoneDisplay(phone)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setPhone(raw);
                  setError('');
                  saveDraft({ phone: raw });
                }}
                autoComplete="tel"
                inputMode="tel"
              />
              <label htmlFor="email" className="sr-only">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Email Address"
                className="meetandgreetmodal-input email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                  saveDraft({ email: e.target.value });
                }}
                autoComplete="email"
              />
            </div>
          </div>

          {/* Date/Time Section */}
          <div className="meetandgreetmodal-datetime-section">
            <label htmlFor="meetDateTime" className="meetandgreetmodal-label time">
              When would you like to visit?
            </label>
            <input
              id="meetDateTime"
              name="meetDateTime"
              type="datetime-local"
              className="meetandgreetmodal-input date"
              value={meetDateTime}
              step={15 * 60}
              onChange={(e) => {
                setMeetDateTime(e.target.value);
                setError('');
                saveDraft({ meetDateTime: e.target.value });
              }}
              required
              aria-required="true"
            />
          </div>

          <div className="dog-cards-carousel">
            <div
              className="dog-cards-wrapper"
              style={{ transform: `translateX(-${currentDogIndex * 100}%)` }}
            >
              {dogs.map((dog) => (
                <div key={dog.id} className="dog-card-slide">
                  <DogCard
                    index={dogs.indexOf(dog)}
                    dog={dog}
                    onDogChange={handleDogChange}
                    removable={dogs.length > 1}
                    onRemove={() => removeDog(dogs.indexOf(dog))}
                    slim={true}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Pagination Dots */}
          {dogs.length > 1 && (
            <div className="dog-pagination">
              {dogs.map((dog, index) => (
                <button
                  key={dog.id}
                  type="button"
                  className={`dog-pagination-dot ${index === currentDogIndex ? 'active' : ''}`}
                  onClick={() => setCurrentDogIndex(index)}
                  aria-label={`Go to dog ${index + 1}`}
                />
              ))}
            </div>
          )}

          <div className="dog-navigation">
            <button
              type="button"
              onClick={handlePrevDog}
              className="dog-nav-button"
              disabled={currentDogIndex === 0}
              aria-label="Previous dog"
            >
              &#8592;
            </button>
            <span>
              {currentDogIndex + 1} of {dogs.length}
            </span>
            <button
              type="button"
              onClick={handleNextDog}
              className="dog-nav-button"
              disabled={currentDogIndex === dogs.length - 1}
              aria-label="Next dog"
            >
              &#8594;
            </button>
          </div>

          <button type="button" onClick={addDog} className="add-dog-button">
            Add Another Dog
          </button>

          <div className="meetandgreetmodal-button-group">
            <button
              type="button"
              className="meetandgreetmodal-button-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="meetandgreetmodal-button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Schedule'}
            </button>
          </div>
        </form>
      </div>
    </ModalWrapper>
  );
};

export default MeetAndGreetModal;
